/**
 * Lumen design tokens: the single source of truth for every visual value (see DESIGN.md).
 * Components reference them only through CSS custom properties (`var(--lm-…)`).
 */

const blockColorNames = ['gray', 'brown', 'orange', 'yellow', 'green', 'blue', 'purple', 'pink', 'red'] as const;
export type BlockColorName = (typeof blockColorNames)[number];
export const BLOCK_COLORS: readonly BlockColorName[] = blockColorNames;

type Palette = Record<BlockColorName, string>;

interface Scheme {
  color: {
    bg: { default: string; sidebar: string; popover: string; codeBlock: string; inlineCode: string; overlay: string; callout: string; tooltip: string; banner: string; input: string; selectedRow: string };
    text: { default: string; secondary: string; tertiary: string; sidebar: string; inlineCode: string; onAccent: string; tooltip: string; danger: string; link: string };
    border: { divider: string; input: string; strong: string; focus: string };
    hover: string;
    active: string;
    selection: { text: string; block: string };
    accent: { blue: string; blueHover: string; red: string; redHover: string; yellowHighlight: string; yellowHighlightActive: string; synced: string };
    linkUnderline: string;
    blockText: Palette;
    blockBg: Palette;
    option: Palette & { default: string };
    presence: readonly string[];
  };
  shadow: { menu: string; modal: string; button: string; card: string; focus: string; sidebarPeek: string; avatarRing: string };
}

export const tokens = {
  font: {
    sans: 'ui-sans-serif, -apple-system, BlinkMacSystemFont, "Segoe UI Variable Display", "Segoe UI", Helvetica, "Apple Color Emoji", Arial, sans-serif, "Segoe UI Emoji", "Segoe UI Symbol"',
    serif: 'Lyon-Text, Georgia, ui-serif, serif',
    mono: 'iawriter-mono, Nitti, Menlo, Courier, monospace',
    code: '"SFMono-Regular", Menlo, Consolas, "PT Mono", "Liberation Mono", Courier, monospace',
  },
  size: {
    body: '16px',
    small: '14px',
    ui: '14px',
    uiSmall: '12px',
    uiTiny: '11px',
    title: '40px',
    titleMobile: '32px',
    h1: '1.875em',
    h1Px: '30px',
    h2: '1.5em',
    h2Px: '24px',
    h3: '1.25em',
    h3Px: '20px',
    codeBlock: '85%',
    inlineCode: '85%',
    pageIcon: '78px',
    sidebarIcon: '18px',
    emoji: '24px',
    emojiSmall: '18px',
    emojiTiny: '16px',
    searchInput: '18px',
    heading: '22px',
    sample: '24px',
  },
  weight: { regular: '400', medium: '500', semibold: '600', bold: '700' },
  lineHeight: { body: '1.5', heading: '1.3', title: '1.2', ui: '20px', tight: '1.2' },
  layout: {
    sidebarWidth: '240px',
    sidebarMin: '160px',
    sidebarMax: '480px',
    topbarHeight: '45px',
    pageMaxWidth: '900px',
    pagePaddingX: '96px',
    pagePaddingXMobile: '24px',
    coverHeight: '30vh',
    coverMaxHeight: '280px',
    blockPaddingY: '3px',
    blockGap: '1px',
    indent: '24px',
    sidebarIndent: '12px',
    handleWidth: '18px',
    handleHeight: '24px',
    menuWidth: '324px',
    contextMenuWidth: '265px',
    mobileBreakpoint: '640px',
    rowHeight: '28px',
    tableRowHeight: '33px',
    columnGap: '46px',
  },
  radius: { xs: '2px', sm: '3px', md: '4px', lg: '6px', xl: '12px', round: '9999px' },
  motion: {
    hover: 'background 20ms ease-in',
    fade: 'opacity 150ms ease',
    menu: 'opacity 100ms ease-out, transform 100ms ease-out',
    sidebar: 'width 300ms cubic-bezier(0.2, 0, 0, 1), transform 300ms cubic-bezier(0.2, 0, 0, 1)',
    toggle: 'transform 200ms ease-out',
    modal: 'opacity 200ms ease, transform 200ms ease',
    flash: 'background 1000ms ease',
    flashFade: 'lm-flash-fade 1.8s ease-out forwards',
    layout: 'top 150ms ease',
    /** Card and button lift on hover (landing page). */
    lift: 'transform 200ms cubic-bezier(0.2, 0, 0, 1), box-shadow 200ms cubic-bezier(0.2, 0, 0, 1)',
    /** Scroll-triggered reveal (landing page): fade up, decelerating. */
    reveal: 'opacity 700ms cubic-bezier(0.16, 1, 0.3, 1), transform 700ms cubic-bezier(0.16, 1, 0.3, 1)',
    durationMenu: '100ms',
    durationHover: '20ms',
    /** Keyframe durations, so component CSS never spells a time out (P-549). */
    durationXs: '160ms',
    durationSm: '320ms',
    durationMd: '600ms',
    durationLg: '1200ms',
    durationXl: '2400ms',
    /** One pass of a looping demo, and the slowest ambient movement (background glow). */
    durationLoop: '9s',
    durationAmbient: '18s',
  },
  /** Cover gallery (P-059): Lumen's own gradient and colour images (apps/web/public/covers). */
  covers: [
    '/covers/sunrise.svg',
    '/covers/sky.svg',
    '/covers/meadow.svg',
    '/covers/lilac.svg',
    '/covers/peach.svg',
    '/covers/lagoon.svg',
    '/covers/dusk.svg',
    '/covers/mint.svg',
    '/covers/deep.svg',
    '/covers/flare.svg',
    '/covers/ember.svg',
    '/covers/sun.svg',
    '/covers/forest.svg',
    '/covers/ocean.svg',
    '/covers/plum.svg',
    '/covers/ink.svg',
  ],
  z: { base: '0', sticky: '10', sidebar: '100', topbar: '101', popover: '2000', modal: '1000', toast: '3000', tooltip: '4000' },
  light: {
    color: {
      bg: {
        default: '#ffffff',
        sidebar: 'rgb(247, 247, 245)',
        popover: '#ffffff',
        codeBlock: 'rgb(247, 246, 243)',
        inlineCode: 'rgba(135, 131, 120, 0.15)',
        overlay: 'rgba(15, 15, 15, 0.6)',
        callout: 'rgb(241, 241, 239)',
        tooltip: 'rgb(15, 15, 15)',
        banner: 'rgb(235, 87, 87)',
        input: 'rgba(242, 241, 238, 0.6)',
        selectedRow: 'rgba(35, 131, 226, 0.07)',
      },
      text: {
        default: 'rgb(55, 53, 47)',
        secondary: 'rgba(55, 53, 47, 0.65)',
        tertiary: 'rgba(55, 53, 47, 0.5)',
        sidebar: 'rgb(95, 94, 91)',
        inlineCode: '#eb5757',
        onAccent: '#ffffff',
        tooltip: 'rgba(255, 255, 255, 0.9)',
        danger: 'rgb(235, 87, 87)',
        link: 'rgb(35, 131, 226)',
      },
      border: {
        divider: 'rgba(55, 53, 47, 0.09)',
        input: 'rgba(15, 15, 15, 0.1)',
        strong: 'rgba(55, 53, 47, 0.16)',
        focus: 'rgba(35, 131, 226, 0.57)',
      },
      hover: 'rgba(55, 53, 47, 0.08)',
      active: 'rgba(55, 53, 47, 0.16)',
      selection: { text: 'rgba(35, 131, 226, 0.28)', block: 'rgba(35, 131, 226, 0.14)' },
      accent: {
        blue: 'rgb(35, 131, 226)',
        blueHover: 'rgb(0, 117, 211)',
        red: 'rgb(235, 87, 87)',
        redHover: 'rgb(224, 62, 62)',
        yellowHighlight: 'rgba(255, 212, 0, 0.14)',
        yellowHighlightActive: 'rgba(255, 212, 0, 0.56)',
        synced: 'rgb(235, 110, 70)',
      },
      linkUnderline: 'rgba(55, 53, 47, 0.4)',
      blockText: {
        gray: 'rgb(120, 119, 116)',
        brown: 'rgb(159, 107, 83)',
        orange: 'rgb(217, 115, 13)',
        yellow: 'rgb(203, 145, 47)',
        green: 'rgb(68, 131, 97)',
        blue: 'rgb(51, 126, 169)',
        purple: 'rgb(144, 101, 176)',
        pink: 'rgb(193, 76, 138)',
        red: 'rgb(212, 76, 71)',
      },
      blockBg: {
        gray: 'rgb(241, 241, 239)',
        brown: 'rgb(244, 238, 238)',
        orange: 'rgb(251, 236, 221)',
        yellow: 'rgb(251, 243, 219)',
        green: 'rgb(237, 243, 236)',
        blue: 'rgb(231, 243, 248)',
        purple: 'rgba(244, 240, 247, 0.8)',
        pink: 'rgba(249, 238, 243, 0.8)',
        red: 'rgb(253, 235, 236)',
      },
      option: {
        default: 'rgba(227, 226, 224, 0.5)',
        gray: 'rgb(227, 226, 224)',
        brown: 'rgb(238, 224, 218)',
        orange: 'rgb(250, 222, 201)',
        yellow: 'rgb(253, 236, 200)',
        green: 'rgb(219, 237, 219)',
        blue: 'rgb(211, 229, 239)',
        purple: 'rgb(232, 222, 238)',
        pink: 'rgb(245, 224, 233)',
        red: 'rgb(255, 226, 221)',
      },
      presence: ['rgb(235, 87, 87)', 'rgb(68, 131, 97)', 'rgb(51, 126, 169)', 'rgb(144, 101, 176)', 'rgb(217, 115, 13)', 'rgb(193, 76, 138)'],
    },
    shadow: {
      menu: 'rgba(15, 15, 15, 0.05) 0 0 0 1px, rgba(15, 15, 15, 0.1) 0 3px 6px, rgba(15, 15, 15, 0.2) 0 9px 24px',
      modal: 'rgba(15, 15, 15, 0.05) 0 0 0 1px, rgba(15, 15, 15, 0.1) 0 5px 10px, rgba(15, 15, 15, 0.2) 0 15px 40px',
      button: 'rgba(15, 15, 15, 0.1) 0 0 0 1px inset, rgba(15, 15, 15, 0.1) 0 1px 2px',
      card: 'rgba(15, 15, 15, 0.1) 0 0 0 1px, rgba(15, 15, 15, 0.1) 0 2px 4px',
      focus: 'rgba(35, 131, 226, 0.57) 0 0 0 1px inset, rgba(35, 131, 226, 0.35) 0 0 0 2px',
      sidebarPeek: 'rgba(15, 15, 15, 0.04) 0 0 0 1px, rgba(15, 15, 15, 0.03) 0 3px 6px, rgba(15, 15, 15, 0.06) 0 9px 24px',
      avatarRing: '#ffffff 0 0 0 2px',
    },
  } satisfies Scheme,
  dark: {
    color: {
      bg: {
        default: 'rgb(25, 25, 25)',
        sidebar: 'rgb(32, 32, 32)',
        popover: 'rgb(37, 37, 37)',
        codeBlock: 'rgba(255, 255, 255, 0.03)',
        inlineCode: 'rgba(135, 131, 120, 0.15)',
        overlay: 'rgba(15, 15, 15, 0.8)',
        callout: 'rgb(47, 47, 47)',
        tooltip: 'rgb(47, 47, 47)',
        banner: 'rgb(235, 87, 87)',
        input: 'rgba(255, 255, 255, 0.055)',
        selectedRow: 'rgba(35, 131, 226, 0.15)',
      },
      text: {
        default: 'rgba(255, 255, 255, 0.81)',
        secondary: 'rgba(255, 255, 255, 0.443)',
        tertiary: 'rgba(255, 255, 255, 0.282)',
        sidebar: 'rgba(255, 255, 255, 0.46)',
        inlineCode: '#eb5757',
        onAccent: '#ffffff',
        tooltip: 'rgba(255, 255, 255, 0.9)',
        danger: 'rgb(235, 87, 87)',
        link: 'rgb(82, 156, 202)',
      },
      border: {
        divider: 'rgba(255, 255, 255, 0.094)',
        input: 'rgba(255, 255, 255, 0.075)',
        strong: 'rgba(255, 255, 255, 0.13)',
        focus: 'rgba(35, 131, 226, 0.57)',
      },
      hover: 'rgba(255, 255, 255, 0.055)',
      active: 'rgba(255, 255, 255, 0.03)',
      selection: { text: 'rgba(35, 131, 226, 0.28)', block: 'rgba(35, 131, 226, 0.2)' },
      accent: {
        blue: 'rgb(35, 131, 226)',
        blueHover: 'rgb(0, 117, 211)',
        red: 'rgb(235, 87, 87)',
        redHover: 'rgb(224, 62, 62)',
        yellowHighlight: 'rgba(255, 212, 0, 0.14)',
        yellowHighlightActive: 'rgba(255, 212, 0, 0.4)',
        synced: 'rgb(235, 110, 70)',
      },
      linkUnderline: 'rgba(255, 255, 255, 0.283)',
      blockText: {
        gray: 'rgb(155, 155, 155)',
        brown: 'rgb(186, 133, 111)',
        orange: 'rgb(199, 125, 72)',
        yellow: 'rgb(202, 152, 73)',
        green: 'rgb(82, 158, 114)',
        blue: 'rgb(94, 135, 201)',
        purple: 'rgb(157, 104, 211)',
        pink: 'rgb(209, 87, 150)',
        red: 'rgb(223, 84, 82)',
      },
      blockBg: {
        gray: 'rgb(47, 47, 47)',
        brown: 'rgb(74, 50, 40)',
        orange: 'rgb(92, 59, 35)',
        yellow: 'rgb(86, 67, 40)',
        green: 'rgb(36, 61, 48)',
        blue: 'rgb(20, 58, 78)',
        purple: 'rgb(60, 45, 73)',
        pink: 'rgb(78, 44, 60)',
        red: 'rgb(82, 46, 42)',
      },
      option: {
        default: 'rgba(55, 55, 55, 0.5)',
        gray: 'rgb(90, 90, 90)',
        brown: 'rgb(96, 59, 44)',
        orange: 'rgb(133, 76, 29)',
        yellow: 'rgb(137, 99, 42)',
        green: 'rgb(43, 89, 63)',
        blue: 'rgb(40, 69, 108)',
        purple: 'rgb(73, 47, 100)',
        pink: 'rgb(105, 49, 76)',
        red: 'rgb(110, 54, 48)',
      },
      presence: ['rgb(235, 87, 87)', 'rgb(82, 158, 114)', 'rgb(94, 135, 201)', 'rgb(157, 104, 211)', 'rgb(199, 125, 72)', 'rgb(209, 87, 150)'],
    },
    shadow: {
      menu: 'rgba(15, 15, 15, 0.1) 0 0 0 1px, rgba(15, 15, 15, 0.2) 0 3px 6px, rgba(15, 15, 15, 0.4) 0 9px 24px',
      modal: 'rgba(15, 15, 15, 0.1) 0 0 0 1px, rgba(15, 15, 15, 0.2) 0 5px 10px, rgba(15, 15, 15, 0.4) 0 15px 40px',
      button: 'rgba(255, 255, 255, 0.094) 0 0 0 1px inset, rgba(15, 15, 15, 0.1) 0 1px 2px',
      card: 'rgba(255, 255, 255, 0.094) 0 0 0 1px, rgba(15, 15, 15, 0.2) 0 2px 4px',
      focus: 'rgba(35, 131, 226, 0.57) 0 0 0 1px inset, rgba(35, 131, 226, 0.35) 0 0 0 2px',
      sidebarPeek: 'rgba(15, 15, 15, 0.1) 0 0 0 1px, rgba(15, 15, 15, 0.2) 0 3px 6px, rgba(15, 15, 15, 0.4) 0 9px 24px',
      avatarRing: 'rgb(25, 25, 25) 0 0 0 2px',
    },
  } satisfies Scheme,
} as const;

export type Tokens = typeof tokens;

function kebab(s: string): string {
  return s.replace(/[A-Z]/g, (c) => `-${c.toLowerCase()}`);
}

function flatten(obj: unknown, prefix: string, out: Record<string, string>): void {
  if (typeof obj === 'string') {
    out[prefix] = obj;
    return;
  }
  if (Array.isArray(obj)) {
    obj.forEach((v, i) => flatten(v, `${prefix}-${i}`, out));
    return;
  }
  if (obj && typeof obj === 'object') {
    for (const [k, v] of Object.entries(obj)) flatten(v, prefix ? `${prefix}-${kebab(k)}` : kebab(k), out);
  }
}

/**
 * Text sizes in pixels follow `--lm-text-scale` (default 1): the iOS app sets it from the system text size
 * (P-847), since its page can't be pinch-zoomed. Sizes in em or % already follow the text around them;
 * icon sizes stay fixed.
 */
const SCALED_TEXT = /^(size-(body|small|ui|ui-small|ui-tiny|title|title-mobile|h[123]-px|emoji|emoji-small|emoji-tiny|search-input|heading|sample)|line-height-ui)$/;

/** Custom properties shared by both schemes (fonts, sizes, layout, radii, motion, z). */
export function staticVars(): Record<string, string> {
  const out: Record<string, string> = {};
  const { light: _l, dark: _d, covers: _c, ...rest } = tokens;
  flatten(rest, '', out);
  return Object.fromEntries(
    Object.entries(out).map(([k, v]) => [`--lm-${k}`, SCALED_TEXT.test(k) && /^\d+(\.\d+)?px$/.test(v) ? `calc(${v} * var(--lm-text-scale, 1))` : v]),
  );
}

/** Custom properties for one colour scheme. */
export function schemeVars(scheme: 'light' | 'dark'): Record<string, string> {
  const out: Record<string, string> = {};
  flatten(tokens[scheme], '', out);
  return Object.fromEntries(Object.entries(out).map(([k, v]) => [`--lm-${k}`, v]));
}

/** Full stylesheet: `:root` static vars, light scheme default, dark scheme under `[data-theme="dark"]`. */
export function tokensCss(): string {
  const decl = (vars: Record<string, string>) =>
    Object.entries(vars)
      .map(([k, v]) => `  ${k}: ${v};`)
      .join('\n');
  return [
    `:root {\n${decl(staticVars())}\n${decl(schemeVars('light'))}\n  color-scheme: light;\n}`,
    `[data-theme="dark"] {\n${decl(schemeVars('dark'))}\n  color-scheme: dark;\n}`,
  ].join('\n');
}

/** Installs the token stylesheet once into the document head. */
export function installTokens(doc: Document): void {
  if (doc.getElementById('lm-tokens')) return;
  const style = doc.createElement('style');
  style.id = 'lm-tokens';
  style.textContent = tokensCss();
  doc.head.prepend(style);
}
